import { useEffect } from 'react';

/**
 * 通用 Modal 壳。
 *
 * Props:
 *   open       boolean  - 显隐
 *   onClose    fn       - 点遮罩 / 右上角 × / 按 ESC
 *   title      string?  - 标题（可选）
 *   children   ReactNode - body
 *   footer     ReactNode? - 右下角按钮区
 *   maxWidth   number?  - 默认 440
 *   closeOnBackdrop boolean? - 默认 true，设 false 则只能点按钮关闭
 *   closeOnEsc boolean? - 默认 true，按 ESC 触发 onClose
 *
 * 行为：
 *   - open=true 时锁住 body 滚动（避免长内容页背景跟着滚），关闭时还原
 *   - 多层 Modal 同时开时，每层各自管理 body overflow，按"最后一层关闭"恢复
 *     （实现：每层进入时记录原值，退出时只在自己仍是同一原值持有者时还原 ——
 *     简单起见这里只做单层最佳化，多层同开非本组件目标场景）
 *
 * 样式与 components/WorkDirectionModal 保持一致（同一套圆角/阴影/遮罩），
 * 后续可考虑把 WorkDirectionModal 也迁到这个壳上。
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 440,
  closeOnBackdrop = true,
  closeOnEsc = true,
}) {
  // ESC 关闭
  useEffect(() => {
    if (!open || !closeOnEsc) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, closeOnEsc, onClose]);

  // body 滚动锁
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  if (!open) return null;

  const handleBackdrop = () => {
    if (closeOnBackdrop) onClose?.();
  };

  return (
    <div style={backdropStyle} onClick={handleBackdrop}>
      <div
        style={{ ...modalStyle, maxWidth }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button onClick={onClose} aria-label="关闭" style={closeBtnStyle}>×</button>
        {title && <h2 style={titleStyle}>{title}</h2>}
        <div>{children}</div>
        {footer && <div style={footerStyle}>{footer}</div>}
      </div>
    </div>
  );
}

const backdropStyle = {
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(15, 23, 42, 0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};

const modalStyle = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  width: '100%',
  padding: '28px 24px',
  position: 'relative',
};

const closeBtnStyle = {
  position: 'absolute', top: 8, right: 10,
  width: 32, height: 32,
  background: 'transparent', border: 'none',
  fontSize: 24, color: '#9ca3af',
  cursor: 'pointer',
};

const titleStyle = {
  fontSize: 20, fontWeight: 700, margin: '0 0 12px',
};

const footerStyle = {
  display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end',
};
